/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Utensils, TrendingUp, TrendingDown, Minus, Target } from "lucide-react";

const TREND = { hausse: ["en hausse", TrendingUp, "#D9534F"], baisse: ["en baisse", TrendingDown, "#5CB85C"], stable: ["stable", Minus, "#0A1426"] };
const Trend = ({ t, invert }) => {
  if (!t) return null;
  const [label, Icon, color] = TREND[t];
  const c = invert ? (t === "hausse" ? "#5CB85C" : t === "baisse" ? "#D9534F" : color) : color;
  return <span className="inline-flex items-center gap-0.5 text-[11px] font-bold" style={{ color: c }}><Icon size={12} /> {label}</span>;
};

/** Équilibre des repas sur 7 jours (pro + patient). */
export function MealScoreCard({ patientId, compact }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/patients/${patientId}/meal-score`).then(({ data }) => setS(data)).catch(() => null); }, [patientId]);
  if (!s) return null;
  const w = s.semaine, rep = w.repartition.glucides;
  const color = w.score == null ? "#0A1426" : w.score >= 7 ? "#5CB85C" : w.score >= 4 ? "#D4AF5A" : "#D9534F";
  return (
    <div data-testid="meal-score-card" className={`bg-white border border-[#E4E0D5] ${compact ? "rounded-3xl p-5" : "rounded-2xl p-4"}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={compact ? "font-[Fraunces] text-xl font-bold text-[#0A1426] flex items-center gap-2" : "text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold flex items-center gap-2"}><Utensils size={compact ? 20 : 14} className="text-[#D4AF5A]" /> Équilibre des repas — 7 jours</h3>
        <span className="text-[10px] text-[#0A1426]/50">{w.photos} photo(s)</span>
      </div>
      {w.photos === 0 ? <div data-testid="meal-score-empty" className="text-[12px] text-[#0A1426]/60">Aucune photo de repas cette semaine.</div> : (
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full grid place-items-center shrink-0 border-4" style={{ borderColor: color }}>
            <div className="text-center leading-none"><div data-testid="meal-score-value" className="font-[Fraunces] text-2xl font-bold" style={{ color }}>{w.score ?? "—"}</div><div className="text-[9px] text-[#0A1426]/60">/10</div></div>
          </div>
          <div className="flex-1 min-w-0 text-[12px] text-[#0A1426] flex flex-col gap-1">
            <div>Équilibre <Trend t={s.tendance_score} invert /></div>
            <div className="flex gap-1 flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-[#5CB85C]/15 text-[#3d8b3d] font-bold text-[10px]">{rep.faible} léger</span>
              <span className="px-2 py-0.5 rounded-full bg-[#D4AF5A]/15 text-[#8A6D2B] font-bold text-[10px]">{rep.moyen} moyen</span>
              <span className="px-2 py-0.5 rounded-full bg-[#D9534F]/15 text-[#b23a36] font-bold text-[10px]">{rep.eleve} riche</span>
            </div>
            {w.glycemie_moyenne && <div data-testid="meal-score-glyc">Glycémie moyenne associée : <b>{w.glycemie_moyenne} mg/dL</b> <Trend t={s.tendance_glycemie} /></div>}
            {s.aliments_frequents.length > 0 && <div className="text-[#0A1426]/70">Souvent : {s.aliments_frequents.join(", ")}</div>}
            <div data-testid="meal-score-comment" className="italic text-[#0A1426]/80">{s.commentaire}</div>
          </div>
        </div>
      )}
      {s.objectif && (
        <div data-testid="meal-goal" className={`mt-3 rounded-2xl p-3 ${s.objectif.atteint ? "bg-[#5CB85C]/15" : "bg-[#F5F3EE]"}`}>
          <div className="flex items-center justify-between text-[12px] text-[#0A1426]">
            <span className="font-bold flex items-center gap-1"><Target size={14} className="text-[#2BA3A0]" /> Objectif du médecin : ≥ {s.objectif.score_cible}/10</span>
            <span data-testid="meal-goal-state" className="font-bold" style={{ color: s.objectif.atteint ? "#3d8b3d" : "#8A6D2B" }}>{s.objectif.atteint ? "Atteint — bravo !" : `${s.objectif.progression} %`}</span>
          </div>
          <div className="h-2.5 rounded-full bg-white mt-2 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${s.objectif.progression}%`, backgroundColor: s.objectif.atteint ? "#5CB85C" : "#D4AF5A" }} /></div>
          {s.objectif.message && <div className="text-[11px] text-[#0A1426]/80 mt-1.5 italic">« {s.objectif.message} » — {s.objectif.par}</div>}
        </div>
      )}
    </div>
  );
}

/** Historique 8 semaines : barres score, ligne objectif, points glycémie. */
export function MealHistoryChart({ patientId }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/patients/${patientId}/meal-history?semaines=8`).then(({ data }) => setH(data)).catch(() => null); }, [patientId]);
  if (!h) return null;
  const W = 560, H = 190, padL = 30, padR = 44, padB = 34, padT = 14;
  const n = h.semaines.length, bw = (W - padL - padR) / n;
  const y = (v) => padT + (1 - v / 10) * (H - padT - padB);
  const glyc = h.semaines.map((w) => w.glycemie_moyenne).filter(Boolean);
  const gMin = glyc.length ? Math.min(...glyc, 70) : 70, gMax = glyc.length ? Math.max(...glyc, 200) : 200;
  const yg = (v) => padT + (1 - (v - gMin) / (gMax - gMin)) * (H - padT - padB);
  const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  return (
    <div data-testid="meal-history-card" className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold flex items-center gap-2"><Utensils size={14} className="text-[#D4AF5A]" /> Historique repas — 8 semaines</h3>
        <span data-testid="meal-history-count" className="text-[12px] font-bold text-[#0A1426]">{h.cible ? `${h.atteintes} semaine(s) atteinte(s) sur ${h.evaluees} · objectif ≥ ${h.cible}/10` : `${h.evaluees} semaine(s) évaluée(s) · pas d'objectif fixé`}</span>
      </div>
      {h.evaluees === 0 ? <div data-testid="meal-history-empty" className="text-[12px] text-[#0A1426]/60">Aucune photo analysée sur la période.</div> : (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Score repas par semaine">
          {[0, 5, 10].map((v) => <g key={v}><line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="#F5F3EE" /><text x={padL - 6} y={y(v) + 4} fontSize="10" textAnchor="end" fill="#0A1426" opacity="0.6">{v}</text></g>)}
          {h.cible && <line x1={padL} x2={W - padR} y1={y(h.cible)} y2={y(h.cible)} stroke="#2BA3A0" strokeDasharray="5 4" strokeWidth="2" />}
          {h.cible && <text x={W - padR + 4} y={y(h.cible) + 4} fontSize="10" fill="#2BA3A0" fontWeight="bold">cible</text>}
          {h.semaines.map((w, i) => {
            const x = padL + i * bw + bw * 0.18, wd = bw * 0.64;
            const color = w.score == null ? "#E4E0D5" : w.atteint ? "#5CB85C" : h.cible ? "#D4AF5A" : "#2BA3A0";
            return (
              <g key={w.debut} data-testid="meal-history-bar">
                {w.score != null ? <rect x={x} y={y(w.score)} width={wd} height={y(0) - y(w.score)} rx="4" fill={color} /> : <rect x={x} y={y(0) - 3} width={wd} height="3" rx="1.5" fill="#E4E0D5" />}
                {w.score != null && <text x={x + wd / 2} y={y(w.score) - 4} fontSize="10" textAnchor="middle" fill="#0A1426" fontWeight="bold">{w.score}</text>}
                <text x={x + wd / 2} y={H - padB + 14} fontSize="9" textAnchor="middle" fill="#0A1426" opacity="0.7">{fmt(w.debut)}</text>
                <text x={x + wd / 2} y={H - padB + 25} fontSize="8" textAnchor="middle" fill="#0A1426" opacity="0.5">{w.photos} ph.</text>
                {w.glycemie_moyenne && <circle cx={x + wd / 2} cy={yg(w.glycemie_moyenne)} r="4" fill="#D9534F" stroke="white" strokeWidth="1.5" />}
              </g>
            );
          })}
          {glyc.length > 0 && <text x={W - padR + 4} y={padT + 8} fontSize="9" fill="#D9534F">● glycémie</text>}
        </svg>
      )}
      <div className="flex gap-3 text-[10px] text-[#0A1426]/70 mt-1 flex-wrap"><span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#5CB85C] mr-1" />objectif atteint</span><span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#D4AF5A] mr-1" />non atteint</span><span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#D9534F] mr-1" />glycémie moyenne (mg/dL)</span></div>
    </div>
  );
}

const GoalField = ({ form, setForm, k, label, type = "number", step }) => (
  <label className="text-[12px] text-[#0A1426]/70 flex flex-col gap-1">{label}
    <input data-testid={`meal-goal-input-${k}`} type={type} step={step} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      className="h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] font-bold" />
  </label>
);

/** Fiche pro : objectif d'équilibre repas hebdo + message. */
export function MealGoalForm({ patientId }) {
  const [form, setForm] = useState({ score_cible: "", message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/patients/${patientId}`).then(({ data: p }) => setForm({ score_cible: p.objectif_repas_score ?? "", message: p.objectif_repas_message ?? "" })).catch(() => null);
  }, [patientId]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/patients/${patientId}/meal-goal`, { score_cible: form.score_cible === "" ? null : Number(form.score_cible), message: form.message || null });
      toast.success("Objectif repas enregistré — visible dans Ma santé du patient");
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Enregistrement impossible"); }
    setBusy(false);
  };
  return (
    <div data-testid="meal-goal-form" className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
      <h3 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-3 flex items-center gap-2"><Target size={14} /> Objectif repas hebdo</h3>
      <div className="grid grid-cols-[110px_1fr] gap-2">
        <GoalField form={form} setForm={setForm} k="score_cible" label="Équilibre ≥ (/10)" step={0.5} />
        <GoalField form={form} setForm={setForm} k="message" label="Message au patient" type="text" />
      </div>
      <p className="text-[11px] text-[#0A1426]/60 mt-2">Chaque lundi, si l'objectif de la semaine est atteint : push de félicitations + badge « Assiette équilibrée ».</p>
      <Button data-testid="meal-goal-save" disabled={busy} onClick={save} className="w-full h-11 mt-3 rounded-full bg-[#0A1426] text-white font-bold">Enregistrer l'objectif</Button>
    </div>
  );
}
